<script setup lang="ts">
import { ElIcon } from 'element-plus'
import { computed } from 'vue'

import { BoxComponent, FlexComponent, TypographyComponent } from '../../..'
import ExternalLinkIcon from '../../../../assets/icons/external-link.svg?component'
import type { AffiliateLinkMetrics, ContentItemLinkDetails } from '../../../../types'
import { isInternalLink } from '../../../../utils'
import { useContentItemLinks } from '../composables'
import type { ViewMode } from '../content-item-details.types'
import DeletableCampaignItem from '../DeletableCampaignItem.vue'

import ContentItemDetailRow from './ContentItemDetailRow.vue'
const props = defineProps<{
  data: ContentItemLinkDetails
  viewMode: ViewMode
  affiliateLinkMetrics?: AffiliateLinkMetrics
}>()
const numberOfClicks = computed(() =>
  props.affiliateLinkMetrics ? props.affiliateLinkMetrics.clicks : props.data.affiliateLinkStats?.numberOfClicks,
)
const emits = defineEmits<{
  (event: 'openAddCampaignModal'): void
  (event: 'openRemoveCampaignModal', campaignId: number): void
}>()
const {
  creatorLink,
  creatorLinkComponent,
  creatorLinkProps,
  advertiserLink,
  affiliateLinkDetailsLink,
  getCampaignLink,
  campaigns,
  getCampaignDisplayName,
  canAddCampaign,
  showNoCampaigns,
} = useContentItemLinks({ data: () => props.data, viewMode: () => props.viewMode })
</script>
<template>
  <BoxComponent>
    <BoxComponent :mb="24">
      <TypographyComponent variant="headline-small">Overview</TypographyComponent>
    </BoxComponent>
    <FlexComponent :gap="16" direction="column">
      <ContentItemDetailRow label="Creator">
        <component :is="creatorLinkComponent" v-if="data.creator && creatorLink" v-bind="creatorLinkProps">
          <TypographyComponent variant="body-medium" text-decoration="underline" cursor="pointer">
            {{ data.creator.username }}
          </TypographyComponent>
        </component>
        <TypographyComponent v-else-if="data.creator" variant="body-medium">{{ data.creator.username }}</TypographyComponent>
        <TypographyComponent v-else variant="body-medium">{{ '—' }}</TypographyComponent>
      </ContentItemDetailRow>
      <ContentItemDetailRow v-if="viewMode !== 'advertiser'" label="Advertiser">
        <router-link v-if="data.advertiser && advertiserLink && isInternalLink(advertiserLink)" :to="advertiserLink">
          <TypographyComponent variant="body-medium" text-decoration="underline" cursor="pointer">
            {{ data.advertiser.name }}
          </TypographyComponent>
        </router-link>
        <a v-else-if="data.advertiser && advertiserLink" :href="advertiserLink">
          <TypographyComponent variant="body-medium" text-decoration="underline" cursor="pointer">
            {{ data.advertiser.name }}
          </TypographyComponent>
        </a>
        <TypographyComponent v-else-if="data.advertiser" variant="body-medium">{{ data.advertiser.name }}</TypographyComponent>
        <TypographyComponent v-else variant="body-medium">{{ '—' }}</TypographyComponent>
      </ContentItemDetailRow>
      <ContentItemDetailRow align="top" :label="campaigns.length > 1 ? 'Campaigns' : 'Campaign'" class="content-item-campaign-row">
        <FlexComponent direction="column" align="stretch" grow="1" :gap="8">
          <BoxComponent v-for="campaign in campaigns" :key="campaign.id" :title="getCampaignDisplayName(campaign)">
            <DeletableCampaignItem
              :campaign-id="campaign.id"
              :campaign-link="getCampaignLink(campaign.id)"
              :is-internal-link="isInternalLink(getCampaignLink(campaign.id))"
              :is-deletable="viewMode === 'admin'"
              @delete="emits('openRemoveCampaignModal', $event)"
            >
              {{ getCampaignDisplayName(campaign) }}
            </DeletableCampaignItem>
          </BoxComponent>
          <TypographyComponent
            :is="'button'"
            v-if="canAddCampaign"
            variant="body-medium"
            text-align="right"
            type="button"
            cursor="pointer"
            class="content-item-details__add-campaign"
            @click="emits('openAddCampaignModal')"
          >
            + Add campaign
          </TypographyComponent>
          <TypographyComponent v-if="showNoCampaigns" variant="body-medium" text-align="right">{{ '—' }}</TypographyComponent>
        </FlexComponent>
      </ContentItemDetailRow>
      <ContentItemDetailRow label="Link">
        <a
          v-if="data.affiliateLink"
          :href="affiliateLinkDetailsLink ?? data.affiliateLink.originalUrl"
          class="content-item-details__link content-item-details__external-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          <TypographyComponent variant="body-medium" text-decoration="underline" cursor="pointer">
            {{ data.affiliateLink.id }}
          </TypographyComponent>
          <ElIcon color="var(--primary)">
            <ExternalLinkIcon />
          </ElIcon>
        </a>
        <TypographyComponent v-else variant="body-medium">—</TypographyComponent>
      </ContentItemDetailRow>
      <ContentItemDetailRow label="Clicks">
        <TypographyComponent variant="body-medium">{{ numberOfClicks || '—' }}</TypographyComponent>
      </ContentItemDetailRow>
    </FlexComponent>
    <el-divider />
  </BoxComponent>
</template>
<style scoped lang="scss">
.content-item-details__link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
:deep(.content-item-details__external-link) {
  path {
    fill: currentColor;
  }
}
.content-item-details__add-campaign {
  padding: 0;
  border: none;
  background: none;
  color: var(--secondary-48);
  &:hover {
    text-decoration: underline;
  }
}
</style>
